import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ScriptableLineSegmentContext,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { isEqual } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

// Set global font defaults
ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

function SoCGraph({ carColour }: { carColour: string }) {
  const raceData = useSelector((state: RootState) => state.raceData, isEqual);

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );
  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );
  const strategyImport = useSelector(
    (state: RootState) => state.imports.imports.strategy?.data,
    isEqual,
  );

  const currentLap = useSelector(
    (state: RootState) => state.raceData[carColour]?.raceConfig?.currentLap,
  );

  const chartData = useMemo(() => {
    const referenceTable = raceData[carColour]?.lapTable ?? {};

    const lapNumbers = Object.keys(referenceTable).map((lapNum) =>
      parseInt(lapNum, 10),
    );

    const datasets = Object.entries(jagDrivers).map(
      ([driverCarColour, driverNumber]) => {
        const driver = driverNumber ? driverInfo[driverNumber] : undefined;
        const driverName = driver?.driverName;
        const driverColour = driver?.driverHighlightColourAlt || "#888";

        const driverTable = raceData[driverCarColour]?.lapTable;
        const dataPoints: { x: number; y: number }[] = [];
        const isRealPoint: boolean[] = [];

        if (driverTable) {
          for (const lapNum of lapNumbers) {
            const driverLap = driverTable[lapNum];
            const value = driverLap?.SoC ?? driverLap?.predictedSoC ?? null;

            if (value !== null) {
              dataPoints.push({ x: lapNum, y: value });
              isRealPoint.push(driverLap?.SoC !== null);
            }
          }
        }

        const pointColours = isRealPoint.map((isReal) =>
          isReal
            ? driverColour
            : `color-mix(in srgb, ${driverColour} 25%, transparent)`,
        );

        return {
          label: driverName,
          data: dataPoints,
          backgroundColor: driverColour,
          borderColor: driverColour,
          pointBackgroundColor: pointColours,
          pointBorderColor: pointColours,
          segment: {
            borderColor: (ctx: ScriptableLineSegmentContext) =>
              isRealPoint[ctx.p0DataIndex] && isRealPoint[ctx.p1DataIndex]
                ? driverColour
                : `color-mix(in srgb, ${driverColour} 25%, transparent)`,
          },
          borderWidth: 2,
          pointStyle: "circle" as const,
          pointRadius: 3,
          pointHoverRadius: 5,
          fill: false,
        };
      },
    );

    return {
      labels: lapNumbers,
      datasets: [...datasets],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceData, carColour, jagDrivers, driverInfo]);

  const maxLapNumber = useMemo(() => {
    const lapNumbers = Object.keys(raceData[carColour]?.lapTable ?? {}).map(
      (lapNum) => parseInt(lapNum, 10),
    );
    return lapNumbers.length > 0 ? Math.max(...lapNumbers) : 1;
  }, [raceData, carColour]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    clip: false as const,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#ffffff",
          usePointStyle: true,
          pointStyle: "line" as const,
          generateLabels: (chart: ChartJS) => {
            const labels =
              ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            return labels.map((label) => {
              const dataset = chart.data.datasets[label.datasetIndex ?? 0];
              return {
                ...label,
                fillStyle:
                  (dataset?.backgroundColor as string) ?? label.fillStyle,
                strokeStyle:
                  (dataset?.borderColor as string) ?? label.strokeStyle,
              };
            });
          },
        },
      },
      annotation: {
        annotations: {
          derateLine: {
            type: "line" as const,
            yMin: strategyImport?.rSoCDerateStarts,
            yMax: strategyImport?.rSoCDerateStarts,
            borderColor: "#ff0000",
            borderDash: [6, 6],
            borderWidth: 1,
          },
          currentLapLine: {
            type: "line" as const,
            xMin: currentLap,
            xMax: currentLap,
            borderColor: "rgba(255, 255, 255, 0.3)",
            borderWidth: 1,
          },
          pitWindowOpenLine: {
            type: "line" as const,
            yMin: strategyImport?.rBMSSoCMinMaxPitWindowPrediction,
            yMax: strategyImport?.rBMSSoCMinMaxPitWindowPrediction,
            borderColor: "#69db7c",
            borderWidth: 1,
          },
          pitWindowCloseLine: {
            type: "line" as const,
            yMin: strategyImport?.rBMSSoCMinMinPitWindowPrediction,
            yMax: strategyImport?.rBMSSoCMinMinPitWindowPrediction,
            borderColor: "#69db7c",
            borderWidth: 1,
          },
        },
      },
      title: {
        display: true,
        text: "SoC",
        color: "#ffffff",
        font: {
          size: 16,
          weight: "bold" as const,
        },
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        title: {
          display: true,
          text: "Lap",
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
          stepSize: 1,
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        border: {
          color: "#ffffff",
        },
        min: 0,
        max: maxLapNumber,
      },
      y: {
        title: {
          display: true,
          text: "SoC (%)",
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        border: {
          color: "#ffffff",
        },
        min: 0,
        max: 100,
      },
    },
  };

  return (
    <div className={styles.container}>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
}

export default SoCGraph;
